import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { nutritionAPI } from "@/lib/nutrition-api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SELECT_CONTENT_MAX_H } from "@/components/settings/recipe-select-constants";

interface BrandOption {
  id: number;
  name: string;
}

interface RecipeBrandSelectProps {
  value: string;
  onValueChange: (next: string) => void;
  brands: BrandOption[];
  isLoading?: boolean;
  disabled?: boolean;
  // Flush the in-progress recipe to a draft before the clinician steps into
  // the brand form, so nothing typed on the recipe is lost mid-flow.
  onBeforeCreate?: () => void;
}

/**
 * Brand picker with an "Add new brand" action pinned to the bottom of the
 * dropdown. The action mirrors the shared SelectItem geometry (gap-2.5 /
 * rounded-2xl / py-2 / pr-8 / pl-3) so it reads as part of the menu, but is a
 * plain button — it opens the create form instead of selecting a value.
 * Choosing it saves the recipe draft; a successful create selects the new
 * brand and refreshes the tag-metadata cache.
 */
export const RecipeBrandSelect = ({
  value,
  onValueChange,
  brands,
  isLoading,
  disabled,
  onBeforeCreate,
}: RecipeBrandSelectProps) => {
  const queryClient = useQueryClient();
  const [selectOpen, setSelectOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [brandName, setBrandName] = useState("");

  const createMutation = useMutation({
    mutationFn: (name: string) => nutritionAPI.foodBrands.create({ name }),
    onSuccess: (created) => {
      // Brands flow into the recipe form via the unified tag-metadata query;
      // FoodBrands.tsx reads its own ["food-brands"] list — refresh both.
      queryClient.invalidateQueries({ queryKey: ["tag-metadata"] });
      queryClient.invalidateQueries({ queryKey: ["food-brands"] });
      onValueChange(created.name);
      toast.success(`“${created.name}” added`);
      setBrandName("");
      setDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to add brand");
    },
  });

  const openCreate = () => {
    setSelectOpen(false);
    onBeforeCreate?.();
    setDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = brandName.trim();
    if (!name) {
      toast.error("Please enter a brand name");
      return;
    }
    createMutation.mutate(name);
  };

  return (
    <>
      <Select
        open={selectOpen}
        onOpenChange={setSelectOpen}
        value={value}
        onValueChange={onValueChange}
        disabled={isLoading || disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder={isLoading ? "Loading…" : "Select brand"} />
        </SelectTrigger>
        <SelectContent position="popper" className={SELECT_CONTENT_MAX_H}>
          {brands.map((b) => (
            <SelectItem key={b.id} value={b.name}>
              {b.name}
            </SelectItem>
          ))}
          {/* Pinned footer action — stays reachable while the list scrolls. */}
          <div className="sticky bottom-0 mt-1 border-t border-border bg-popover pt-1">
            <button
              type="button"
              onClick={openCreate}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2.5 rounded-2xl py-2 pr-8 pl-3 text-sm font-medium text-primary outline-none select-none",
                "hover:bg-accent focus-visible:bg-accent active:bg-accent/80",
                "disabled:pointer-events-none disabled:opacity-50",
                "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              )}
            >
              <Plus />
              Add new brand
            </button>
          </div>
        </SelectContent>
      </Select>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add new brand</DialogTitle>
            <DialogDescription>
              Your recipe is saved as a draft — add the brand and it’ll be
              selected when you’re done.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recipe-brand-name">Brand name</Label>
              <Input
                id="recipe-brand-name"
                autoFocus
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="e.g. Aashirvaad"
                disabled={createMutation.isPending}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="size-4 animate-spin" />
                )}
                Add brand
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecipeBrandSelect;
